import {useCallback, useEffect, useRef} from 'react';
import {AuthService, authServiceRtk, type ScreenHint} from '../services/authService.ts';
import {useGetCurrentUserQuery, useGetPortalConfigQuery} from '../store/generated/api';
import {TOKEN_REFRESH_WINDOW_MS} from '../services/tokenProvider';

const TOKEN_CHECK_INTERVAL_MS = 30 * 1000;

export const useAuth = () => {
    const {data, isLoading, error, refetch} = useGetCurrentUserQuery();
    const user = data?.user || null;
    const {data: featureConfig} = useGetPortalConfigQuery();

    const initializationRef = useRef(false);
    const intervalRef = useRef<number | null>(null);
    const authServiceRef = useRef<AuthService>(authServiceRtk);

    const shouldRefreshToken = useCallback((): boolean => {
        const tokenExpiry = authServiceRef.current.getTokenExpiry();
        if (!tokenExpiry) return false;

        return Date.now() >= (tokenExpiry - TOKEN_REFRESH_WINDOW_MS);
    }, []);

    const initializeAuth = useCallback(async () => {
        if (initializationRef.current || isLoading) {
            return;
        }

        initializationRef.current = true;

        try {
            const urlParams = new URLSearchParams(window.location.search);

            if (urlParams.has('token') || urlParams.has('error')) {
                await authServiceRef.current.handleTokensFromUrl();
                return;
            }

            await authServiceRef.current.initializeAuth();
        } catch (err) {
            console.error('Authentication failed:', err);
        }
    }, [isLoading]);

    const login = useCallback(async (redirectUrl?: string, screenHint?: ScreenHint): Promise<void> => {
        try {
            await authServiceRef.current.initiateLogin(redirectUrl, screenHint);
        } catch (err) {
            console.error('Login failed:', err);
        }
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        try {
            await authServiceRef.current.logout();
        } catch (err) {
            console.error('Logout failed:', err);
        }
    }, []);

    const refreshUser = useCallback(async (): Promise<void> => {
        try {
            await refetch();
        } catch (err) {
            console.error('Failed to refresh user information:', err);
        }
    }, [refetch]);

    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(async () => {
            if (authServiceRef.current.isAuthenticated() && shouldRefreshToken()) {
                try {
                    const refreshed = await authServiceRef.current.refreshToken();
                    if (!refreshed) {
                        await authServiceRef.current.logout();
                    }
                } catch (err) {
                    console.error('Token refresh failed:', err);
                }
            }
        }, TOKEN_CHECK_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [shouldRefreshToken]);

    const hasValidTokens = authServiceRef.current.isAuthenticated();
    const isAuthenticated = hasValidTokens && (user !== null || isLoading);

    return {
        user,
        isAuthenticated,
        isLoading,
        error: error ? String(error) : null,
        login,
        logout,
        refreshUser,
        clearError: () => {
        },
        featureConfig,
    };
};